// ThemeContext.tsx
import { ThemeConfig } from 'antd'
// themeConfig.ts
import { theme } from 'antd'
import React, { createContext, useContext, useEffect, useState } from 'react'

const { defaultAlgorithm, darkAlgorithm } = theme

import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '@supplement-bacon/react-toolkit/utils'
type ThemeMode = 'light' | 'dark'
type LayoutMode = 'default' | 'large'

interface ThemeContextProps {
  mode: ThemeMode
  layoutMode: LayoutMode
  toggleLayout: () => void
  toggleTheme: () => void
  themeConfig: ThemeConfig
}

const ThemeContext = createContext<ThemeContextProps | null>(null)

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = getLocalStorageItem('theme') as ThemeMode | null
    document.documentElement.classList.toggle('dark', stored === 'dark')
    return stored ?? 'light'
  })

  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => {
    const stored = getLocalStorageItem('layout') as LayoutMode | null
    document.documentElement.classList.toggle(
      'layout-full-width',
      stored === 'large'
    )
    return stored ?? 'default'
  })

  const toggleTheme = () => {
    document.documentElement.classList.toggle('dark', mode === 'light')
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'))
  }

  const toggleLayout = () => {
    document.documentElement.classList.toggle(
      'layout-full-width',
      layoutMode === 'default'
    )
    setLayoutMode((prev) => (prev === 'default' ? 'large' : 'default'))
  }

  useEffect(() => {
    setLocalStorageItem('theme', mode)
  }, [mode])

  useEffect(() => {
    setLocalStorageItem('layout', layoutMode)
  }, [layoutMode])

  const themeConfig: ThemeConfig = {
    algorithm: mode === 'dark' ? darkAlgorithm : defaultAlgorithm,
    token: {
      colorPrimary: '#8e978a',
      colorLink: '#8e978a',
      colorInfo: '#8e978a',
      colorInfoBorder: '#8e978a',
      colorInfoBg: '#8e978a20',
    },
    components: {
      Collapse: {
        headerBg: mode === 'dark' ? '#141414' : '#fff',
      },
      Segmented: {
        trackBg: mode === 'dark' ? '#333' : '#ededed',
      },
    },
  }

  return (
    <ThemeContext.Provider
      value={{ mode, toggleTheme, layoutMode, toggleLayout, themeConfig }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within ThemeProvider')
  return context
}
